import { Input, Tabs, TabItem, Dropdown, Button, CapsuleTab } from '@/components/atoms/index'
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '@/icons';
import { vars } from 'style/theme.css';
import LearningCard from '@/components/organisms/Card/LearningCard';
import AcademyExamTable from '@/components/organisms/Table/AcademyExamTable';
import VocabularyTable from '@/components/organisms/Table/VocabularyTable';
import TrophyBadgeCard from '@/components/organisms/Card/TrohyBadgeCard';
import { useParams, useNavigate } from 'react-router-dom';
import { academyStudentApi, academyTeacherApi, academyClassApi, academyTrophyBadgeApi } from '@/services/api';
import { useSessionSlug } from '@/hooks/useSessionSlug';
import { message, Modal } from 'antd';

type StudentDetail = {
    academyStudentId: number;
    userId: number;
    name: string;
    studentLoginId: string;
    className: string;
    birthDate?: string;
    status: string;
    registrationDate?: string;
    withdrawnAt?: string;
    gradeLabel?: string;
    parentEmail?: string;
    classRoomId?: number;
    assignedTeacherId?: number;
};

type DropdownOption = { value: string; label: string };

const MIN_PASSWORD_LENGTH = 8;

// 기본 정보 탭 컴포넌트
const BasicInfoTab = ({
    student,
    name,
    setName,
    birthDate,
    setBirthDate,
    parentEmail,
    setParentEmail,
    classOptions,
    classRoomId,
    setClassRoomId,
    teacherOptions,
    assignedTeacherId,
    setAssignedTeacherId,
    onSave,
    saving,
    canEditBasicInfo,
    onWithdraw,
    withdrawing,
    permissionChecked,
    onPasswordChange,
}: {
    student?: StudentDetail;
    name: string;
    setName: (v: string) => void;
    birthDate: string;
    setBirthDate: (v: string) => void;
    parentEmail: string;
    setParentEmail: (v: string) => void;
    classOptions: DropdownOption[];
    classRoomId?: string | null;
    setClassRoomId: (v: string) => void;
    teacherOptions: DropdownOption[];
    assignedTeacherId?: string | null;
    setAssignedTeacherId: (v: string) => void;
    onSave: () => void;
    saving: boolean;
    canEditBasicInfo: boolean;
    onWithdraw?: () => void;
    withdrawing: boolean;
    permissionChecked: boolean;
    onPasswordChange?: () => void;
}) => {
    const regDate = useMemo(() => student?.registrationDate ? String(student.registrationDate).replace('T', ' ').slice(0, 16) : '', [student]);
    const withdrawDate = useMemo(() => student?.withdrawnAt ? String(student.withdrawnAt).replace('T', ' ').slice(0, 16) : '', [student]);
    const inputsDisabled = !permissionChecked || !canEditBasicInfo;
    return (
        <div className="w-full">
            <ul>
                <li className='flex py-5 border-b border-gray-200'>
                    <p className='flex-none max-w-[150px] w-full text-gray-500'>이름</p>
                    <Input placeholder='이름을 입력해주세요' className='max-w-[500px] w-full' value={name} onChange={(e) => setName(e.target.value)} disabled={inputsDisabled} />
                </li>

                <li className='flex py-5 border-b border-gray-200'>
                    <p className='flex-none max-w-[150px] w-full text-gray-500'>생년월일</p>
                    <Input placeholder='생년월일을 입력해주세요 (YYYY-MM-DD)' className='max-w-[500px] w-full' value={birthDate} onChange={(e) => setBirthDate(e.target.value)} disabled={inputsDisabled} />
                </li>

                <li className='flex py-5 border-b border-gray-200'>
                    <p className='flex-none max-w-[150px] w-full text-gray-500'>학부모 이메일</p>
                    <Input placeholder='학부모 이메일을 입력해주세요' className='max-w-[500px] w-full' value={parentEmail} onChange={(e) => setParentEmail(e.target.value)} disabled={inputsDisabled}/>
                </li>

                <li className='flex py-5 border-b border-gray-200'>
                    <p className='flex-none max-w-[150px] w-full text-gray-500'>클래스</p>
                    <Dropdown placeholder='클래스를 선택해주세요' className='max-w-[500px] w-full' options={classOptions} onChange={setClassRoomId} value={classRoomId || null} disabled={inputsDisabled} />
                </li>

                <li className='flex py-5 border-b border-gray-200'>
                    <p className='flex-none max-w-[150px] w-full text-gray-500'>담당선생님</p>
                    <Dropdown placeholder='담당선생님을 선택해주세요' className='max-w-[500px] w-full' options={teacherOptions} onChange={setAssignedTeacherId} value={assignedTeacherId || null} disabled={inputsDisabled} />
                </li>

                <li className='flex py-5 border-b border-gray-200'>
                    <p className='flex-none max-w-[150px] w-full text-gray-500'>가입상태</p>
                    <Input disabled value={student?.status || '-'} className='max-w-[500px] w-full'/>
                </li>

                <li className='flex py-5 border-b border-gray-200'>
                    <p className='flex-none max-w-[150px] w-full text-gray-500'>가입 신청일</p>
                    <Input disabled value={regDate} className='max-w-[500px] w-full'/>
                </li>

                <li className='flex py-5 border-b border-gray-200'>
                    <p className='flex-none max-w-[150px] w-full text-gray-500'>퇴원 처리일</p>
                    <Input disabled value={withdrawDate || '-'} className='max-w-[500px] w-full'/>
                </li>
                {permissionChecked && canEditBasicInfo && (
                    <li className='flex py-5 border-b border-gray-200'>
                        <p className='flex-none max-w-[150px] w-full text-gray-500'>비밀번호 관리</p>
                        <Button onClick={onPasswordChange}>비밀번호 변경</Button>
                    </li>
                )}
            </ul>
            <div className='flex flex-col gap-2 mt-4 lg:flex-row lg:items-center'>
                {!permissionChecked && (
                    <p className='text-sm text-gray-500'>권한 정보를 확인하는 중입니다...</p>
                )}
                {permissionChecked && !canEditBasicInfo && (
                    <p className='text-sm text-gray-500'>강사 권한 계정은 학생 기본 정보를 수정하거나 퇴원 처리할 수 없습니다.</p>
                )}
                {permissionChecked && canEditBasicInfo && (
                    <div className='flex justify-end *:w-[120px] gap-2 ml-auto'>
                        <Button intent='secondary' onClick={onWithdraw} disabled={withdrawing || student?.status === '퇴원'}>
                            {withdrawing ? '퇴원 처리중...' : '퇴원'}
                        </Button>
                        <Button onClick={onSave} disabled={saving}>{saving ? '저장중...' : '저장'}</Button>
                    </div>
                )}
            </div>
        </div>
    );
};

// 학습 이력 탭 컴포넌트 (실데이터 연동)
const LearningHistoryTab = ({ academyStudentId, initialCategory }: { academyStudentId: string, initialCategory?: 'learning' | 'homework' | 'exam' | 'vocabulary' }) => {
    const [activeCategory, setActiveCategory] = useState<'learning' | 'homework' | 'exam' | 'vocabulary'>(initialCategory || 'learning');
    const [showOnlyAvailable, setShowOnlyAvailable] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(false);

    // 학습/숙제 데이터
    const [learningItems, setLearningItems] = useState<Array<{
        id: number;
        title: string;
        units: string;
        progress: number;
        startDate?: string;
        endDate: string;
        status?: 'completed' | 'in-progress' | 'waiting';
        thumbnailUrl?: string;
    }>>([]);

    // 시험 데이터
    const [examRows, setExamRows] = useState<Array<{
        id: number;
        name: string;
        examStatus: '미응시' | '응시완료' | '채점완료';
        examDate?: string;
        score?: number;
    }>>([]);

    // 단어장 데이터
    const [vocabRows, setVocabRows] = useState<Array<{
        id: number;
        name: string;
        wordCount: number;
        learningHistory: string;
        registrationDate: string;
    }>>([]);

    const [page, setPage] = useState(1);
    const pageSize = 10;
    const [totalElements, setTotalElements] = useState(0);
    const navigate = useNavigate();
    
    const tabs: TabItem[] = [
        { id: 'learning', label: '학습', onClick: () => { setActiveCategory('learning'); setPage(1); } },
        { id: 'homework', label: '숙제', onClick: () => { setActiveCategory('homework'); setPage(1); } },
        { id: 'exam', label: '시험', onClick: () => { setActiveCategory('exam'); setPage(1); } },
        { id: 'vocabulary', label: '단어장', onClick: () => { setActiveCategory('vocabulary'); setPage(1); } },
    ];
    
    const handleToggleAvailable = () => {
        setShowOnlyAvailable(!showOnlyAvailable);
    };

    useEffect(() => {
        const fetchList = async () => {
            if (!academyStudentId) return;
            setLoading(true);
            try {
                const res: any = await academyStudentApi.learningHistory({
                    academyStudentId,
                    category: activeCategory,
                    page: page - 1,
                    size: pageSize,
                    search: searchText,
                    onlyAvailable: showOnlyAvailable,
                } as any);
                const data = res?.data || res;
                const content = data?.content || [];
                setTotalElements(Number(data?.totalElements || content.length || 0));
                if (activeCategory === 'learning' || activeCategory === 'homework') {
                    setLearningItems(content as any);
                } else if (activeCategory === 'exam') {
                    setExamRows(content as any);
                } else if (activeCategory === 'vocabulary') {
                    setVocabRows(content as any);
                }
            } finally {
                setLoading(false);
            }
        };
        fetchList();
    }, [academyStudentId, activeCategory, page, pageSize, searchText, showOnlyAvailable]);

    // 현재 서브탭 카테고리를 URL 쿼리에 반영(뒤로가기로 복귀시 보존)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        params.set('category', activeCategory);
        if (!params.get('tab')) params.set('tab', 'learning');
        window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
    }, [activeCategory]);
    const openScheduleDetail = (scheduleId: number) => {
        const qp = new URLSearchParams();
        qp.set('category', activeCategory);
        qp.set('from', academyStudentId);
        navigate(`/academy/student/schedule/${scheduleId}?${qp.toString()}`);
    };

    // 카드 헤더 표시값(제목/상태/진행률) 집계
    const cardHeaderTitle = activeCategory === 'learning' ? '학습 목록' : '숙제 목록';
    const avgProgress = learningItems.length > 0
        ? Math.round(learningItems.reduce((sum, it: any) => sum + (Number(it?.progress ?? 0)), 0) / learningItems.length)
        : 0;
    const allCompleted = learningItems.length > 0 && learningItems.every((it: any) => Number(it?.progress ?? 0) >= 100 || it?.status === 'completed');
    const anyInProgress = learningItems.some((it: any) => it?.status === 'in-progress' || (Number(it?.progress ?? 0) > 0 && Number(it?.progress ?? 0) < 100));
    const headerStatus: 'completed' | 'in-progress' | 'waiting' = allCompleted ? 'completed' : (anyInProgress ? 'in-progress' : 'waiting');

    return (
        <div className="">
            <CapsuleTab tabs={tabs} activeId={activeCategory} />
            {(activeCategory === 'learning' || activeCategory === 'homework') && (
                <>     
            <section className='flex flex-col gap-2 items-start pt-3 w-full lg:flex-row lg:justify-between lg:items-center'>
                <Input
                placeholder='검색어를 입력해주세요.'
                className='max-w-[500px]'
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                icon={<Icon name="search" size={20} color={vars.color.gray[300]} />}
                />
                <button 
                  className={`flex gap-1 justify-center items-center text-sm ${showOnlyAvailable ? 'text-indigo-500' : 'text-gray-500'}`}
                  onClick={handleToggleAvailable}
                >
                    <Icon name="check" size={20} className={showOnlyAvailable ? '*:fill-indigo-500' : '*:fill-gray-400'} />
                    <span>응시 가능한 학습만 보기</span>
                </button>
            </section>
            {learningItems.length > 0 ? (
            <div className='flex flex-col gap-4 py-3'>
                        <LearningCard 
                            title={cardHeaderTitle}
                            status={headerStatus}
                            progress={avgProgress}
                            type={activeCategory as 'learning' | 'homework'}
                            learningItems={learningItems as any}
                            onCardClick={(id) => openScheduleDetail(id)}
                            onItemClick={(id) => openScheduleDetail(id)}
                        />  
                </div>
            ) : (
                <div className='py-8 text-center text-gray-500'>등록된 내역이 없습니다.</div>
            )}
                </>
            )}  

{activeCategory === 'exam' && (
                <>     
                <AcademyExamTable
                    data={examRows as any}
                    loading={loading}
                    currentPage={page}
                    pageSize={pageSize}
                    totalElements={totalElements}
                    onPageChange={(p) => setPage(p)}
                    searchText={searchText}
                    onSearchChange={setSearchText}
                />
                </>
            )}  

{activeCategory === 'vocabulary' && (
                <>     
                <VocabularyTable
                    data={vocabRows as any}
                    loading={loading}
                    disableNavigation={true}
                    currentPage={page}
                    pageSize={pageSize}
                    totalElements={totalElements}
                    onPageChange={(p) => setPage(p)}
                    searchText={searchText}
                    onSearchChange={setSearchText}
                />        
                </>
            )}  
        </div>
    );
};

// 성취도 탭 컴포넌트 (실데이터 연동)
const AchievementTab = ({ academyCode, studentUserId }: { academyCode: string; studentUserId?: number }) => {
    const [activeAchievementTab, setActiveAchievementTab] = useState<'trophy' | 'badge'>('trophy');
    const [trophies, setTrophies] = useState<any[]>([]);
    const [badges, setBadges] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    
    const achievementTabs: TabItem[] = [
        { id: 'trophy', label: '트로피', onClick: () => setActiveAchievementTab('trophy') },
        { id: 'badge', label: '뱃지', onClick: () => setActiveAchievementTab('badge') },
    ];

    useEffect(() => {
        const fetchAwards = async () => {
            if (!academyCode || !studentUserId) return;
            setLoading(true);
            try {
                // 트로피
                const resT: any = await academyTrophyBadgeApi.listAwards({ academyCode, type: 'trophy' });
                const dataT = resT?.data || resT;
                const contentT = (dataT?.content || []).filter((a: any) => Number(a.studentUserId) === Number(studentUserId));
                setTrophies(contentT);
                // 뱃지
                const resB: any = await academyTrophyBadgeApi.listAwards({ academyCode, type: 'badge' });
                const dataB = resB?.data || resB;
                const contentB = (dataB?.content || []).filter((a: any) => Number(a.studentUserId) === Number(studentUserId));
                // 중복 뱃지 수량 집계 (trophyBadgeId 기준)
                const badgeMap = new Map<string | number, any>();
                for (const a of contentB) {
                    const key = a.trophyBadgeId ?? a.id ?? `${a.trophyBadgeName}-${a.iconUrl}`;
                    const prev = badgeMap.get(key);
                    if (!prev) {
                        badgeMap.set(key, {
                            id: a.trophyBadgeId ?? a.id,
                            trophyBadgeId: a.trophyBadgeId,
                            trophyBadgeName: a.trophyBadgeName,
                            iconUrl: a.iconUrl,
                            type: a.type,
                            awardedAt: a.awardedAt,
                            count: 1,
                        });
                    } else {
                        prev.count = (prev.count || 1) + 1;
                        // 최신 수여일로 갱신
                        if (!prev.awardedAt || String(a.awardedAt || '').localeCompare(String(prev.awardedAt)) > 0) {
                            prev.awardedAt = a.awardedAt;
                        }
                    }
                }
                const aggregatedBadges = Array.from(badgeMap.values());
                // 최신 수여일 내림차순 정렬
                aggregatedBadges.sort((a, b) => String(b.awardedAt || '').localeCompare(String(a.awardedAt || '')));
                setBadges(aggregatedBadges);
            } finally {
                setLoading(false);
            }
        };
        fetchAwards();
    }, [academyCode, studentUserId]);

    const renderGrid = (items: any[]) => {
        if (loading) {
            return <div className='py-6 text-gray-500'>로딩 중...</div>;
        }
        if (!items || items.length === 0) {
            return <div className='py-6 text-gray-500'>수여된 내역이 없습니다.</div>;
        }
        return (
            <section className='grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-4'>
                {items.map((it: any) => (
                    <TrophyBadgeCard 
                        key={it.id ?? it.trophyBadgeId}
                        type={it.type === 'trophy' ? 'trophy' : 'badge'}
                        iconUrl={it.iconUrl}
                        name={it.trophyBadgeName}
                        date={(it.awardedAt || '').toString().slice(0, 10)}
                        count={it.count || 1}
                        showLabel={true}
                    />
                ))}
            </section>
        );
    };

    return (
        <> 
            <CapsuleTab tabs={achievementTabs} activeId={activeAchievementTab} />
            {activeAchievementTab === 'trophy' ? renderGrid(trophies) : renderGrid(badges)}
        </>
    );
};

export default function AcademyStudentDetail() {
    const [activeTab, setActiveTab] = useState<'basic' | 'learning' | 'achievement'>('basic');
    const { studentId } = useParams();
    const navigate = useNavigate();
    const [student, setStudent] = useState<StudentDetail | undefined>();
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');
    const [saving, setSaving] = useState<boolean>(false);
    const [withdrawing, setWithdrawing] = useState<boolean>(false);
    const [passwordModalOpen, setPasswordModalOpen] = useState(false);
    const [passwordForm, setPasswordForm] = useState<{ newPassword: string; confirmPassword: string }>({ newPassword: '', confirmPassword: '' });
    const [passwordErrors, setPasswordErrors] = useState<{ newPassword?: string; confirmPassword?: string }>({});
    const [changingPassword, setChangingPassword] = useState(false);

    const { academyCode: sessionAcademyCode, status: sessionStatus, role: sessionRole } = useSessionSlug();
    const academyCode = useMemo(() => sessionAcademyCode || '', [sessionAcademyCode]);
    const [cachedRole, setCachedRole] = useState<string | null>(() => {
        if (typeof window === 'undefined') return null;
        const stored = localStorage.getItem('academy_role');
        return stored ? stored.toUpperCase() : null;
    });
    useEffect(() => {
        if (sessionRole) {
            const normalized = sessionRole.toUpperCase();
            setCachedRole(normalized);
            if (typeof window !== 'undefined') {
                localStorage.setItem('academy_role', normalized);
            }
        }
    }, [sessionRole]);
    const permissionChecked = !!cachedRole;
    const canEditBasicInfo = permissionChecked && cachedRole !== 'TEACHER';
    const [name, setName] = useState('');
    const [birthDate, setBirthDate] = useState('');
    const [parentEmail, setParentEmail] = useState('');
    const [classOptions, setClassOptions] = useState<DropdownOption[]>([]);
    const [teacherOptions, setTeacherOptions] = useState<DropdownOption[]>([]);
    const [classRoomId, setClassRoomId] = useState<string>('');
    const [assignedTeacherId, setAssignedTeacherId] = useState<string>('');

    useEffect(() => {
        // 쿼리스트링의 tab 값으로 초기 탭 설정 지원
        const qp = new URLSearchParams(window.location.search);
        const tab = qp.get('tab') as any;
        if (tab === 'basic' || tab === 'learning' || tab === 'achievement') {
            setActiveTab(tab);
        }
    }, []);

    const fetchDetail = useCallback(async () => {
        if (!studentId) return;
        setLoading(true);
        setError('');
        try {
            const res: any = await academyStudentApi.detail(studentId);
            const data: StudentDetail = (res?.data || res) as StudentDetail;
            setStudent(data);
            setName(data.name || '');
            setBirthDate(data.birthDate ? String(data.birthDate) : '');
            setParentEmail(data.parentEmail || '');
            setClassRoomId(data.classRoomId ? String(data.classRoomId) : '');
            setAssignedTeacherId(data.assignedTeacherId ? String(data.assignedTeacherId) : '');
        } catch (e) {
            setError('학생 정보를 불러오지 못했습니다.');
        } finally {
            setLoading(false);
        }
    }, [studentId]);

    useEffect(() => {
        fetchDetail();
    }, [fetchDetail]);

    useEffect(() => {
        const fetchOptions = async () => {
            if (!academyCode || sessionStatus !== 'ready') return;
            try {
                const [classRes, teacherRes] = await Promise.all([
                    academyClassApi.list({ academyCode, page: 0, size: 100 }),
                    academyTeacherApi.list({ academyCode, page: 0, size: 100 }),
                ]);
                const classPage = classRes?.data || classRes;
                const teacherPage = teacherRes?.data || teacherRes;
                setClassOptions((classPage?.content || []).map((c: any) => ({ value: String(c.id), label: c.name })));
                setTeacherOptions((teacherPage?.content || []).map((t: any) => ({ value: String(t.id), label: t.name })));
            } catch (e) {
                // 옵션 로딩 실패는 무시
            }
        };
        fetchOptions();
    }, [academyCode, sessionStatus]);
    
    const tabItems: TabItem[] = [
        { id: 'basic', label: '기본 정보', onClick: () => setActiveTab('basic') },
        { id: 'learning', label: '학습 내역', onClick: () => setActiveTab('learning') },
        { id: 'achievement', label: '트로피 및 뱃지', onClick: () => setActiveTab('achievement') },
    ];

    const handleSave = async () => {
        if (!studentId) return;
        if (!canEditBasicInfo) {
            message.warning('강사 권한 계정은 학생 정보를 수정할 수 없습니다.');
            return;
        }
        setSaving(true);
        try {
            await academyStudentApi.update(studentId, {
                name: name || null,
                birthDate: birthDate || null,
                parentEmail: parentEmail || null,
                classRoomId: classRoomId ? Number(classRoomId) : null,
                assignedTeacherId: assignedTeacherId ? Number(assignedTeacherId) : null,
            });
            message.success('저장되었습니다.');
            fetchDetail();
        } catch (e) {
            setError('저장에 실패했습니다.');
            message.error('저장에 실패했습니다.');
        } finally {
            setSaving(false);
        }
    };

    const handleWithdraw = useCallback(() => {
        if (!studentId || !canEditBasicInfo) return;
        Modal.confirm({
            title: '해당 학생을 퇴원처리 하겠습니까?',
            okText: '퇴원처리',
            cancelText: '취소',
            okType: 'danger',
            centered: true,
            onOk: async () => {
                setWithdrawing(true);
                try {
                    await academyStudentApi.withdraw(studentId);
                    message.success('퇴원 처리되었습니다.');
                    await fetchDetail();
                } catch (e) {
                    message.error('퇴원 처리에 실패했습니다.');
                } finally {
                    setWithdrawing(false);
                }
            },
        });
    }, [studentId, canEditBasicInfo, fetchDetail]);

    const resetPasswordForm = () => {
        setPasswordForm({ newPassword: '', confirmPassword: '' });
        setPasswordErrors({});
    };

    const handleOpenPasswordModal = () => {
        if (!canEditBasicInfo) return;
        setPasswordModalOpen(true);
    };

    const handleClosePasswordModal = () => {
        if (changingPassword) return;
        setPasswordModalOpen(false);
        resetPasswordForm();
    };

    const handlePasswordFieldChange = (field: 'newPassword' | 'confirmPassword', value: string) => {
        setPasswordForm((prev) => ({ ...prev, [field]: value }));
        setPasswordErrors((prev) => ({ ...prev, [field]: undefined }));
    };

    const validatePasswordForm = () => {
        const errors: { newPassword?: string; confirmPassword?: string } = {};
        const trimmedNewPassword = passwordForm.newPassword.trim();
        const trimmedConfirmPassword = passwordForm.confirmPassword.trim();

        if (!trimmedNewPassword) {
            errors.newPassword = '새 비밀번호를 입력해주세요.';
        } else if (!/^(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[^a-zA-Z0-9]).{8,}$/.test(trimmedNewPassword)) {
            errors.newPassword = '비밀번호는 영문, 숫자, 특수문자를 포함하여 8자 이상이어야 합니다.';
        }

        if (!trimmedConfirmPassword) {
            errors.confirmPassword = '새 비밀번호를 다시 입력해주세요.';
        } else if (trimmedNewPassword !== trimmedConfirmPassword) {
            errors.confirmPassword = '비밀번호가 일치하지 않습니다.';
        }

        setPasswordErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handlePasswordChange = async () => {
        if (!studentId) return;
        if (!validatePasswordForm()) return;

        setChangingPassword(true);
        try {
            await academyStudentApi.changePassword(studentId, { newPassword: passwordForm.newPassword.trim() });
            message.success('비밀번호가 변경되었습니다.');
            setPasswordModalOpen(false);
            resetPasswordForm();
        } catch (e: any) {
            message.error(e?.response?.data?.message || '비밀번호 변경에 실패했습니다.');
        } finally {
            setChangingPassword(false);
        }
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'basic':
                return (
                    <BasicInfoTab
                        student={student}
                        name={name}
                        setName={setName}
                        birthDate={birthDate}
                        setBirthDate={setBirthDate}
                        parentEmail={parentEmail}
                        setParentEmail={setParentEmail}
                        classOptions={classOptions}
                        classRoomId={classRoomId}
                        setClassRoomId={setClassRoomId}
                        teacherOptions={teacherOptions}
                        assignedTeacherId={assignedTeacherId}
                        setAssignedTeacherId={setAssignedTeacherId}
                        onSave={handleSave}
                        saving={saving}
                        canEditBasicInfo={canEditBasicInfo}
                        onWithdraw={handleWithdraw}
                        withdrawing={withdrawing}
                        permissionChecked={permissionChecked}
                        onPasswordChange={handleOpenPasswordModal}
                    />
                );
            case 'learning':
                // URL의 category 쿼리로 서브탭 초기값 설정
                const params = new URLSearchParams(window.location.search);
                const initCat = (params.get('category') as any) || undefined;
                return <LearningHistoryTab academyStudentId={String(studentId)} initialCategory={initCat} />;
            case 'achievement':
                return <AchievementTab academyCode={academyCode} studentUserId={student?.userId} />;
            default:
                return null;
        }
    };

    const handleBack = () => {
        const sp = new URLSearchParams(window.location.search);
        const from = sp.get('from');
        const page = sp.get('page');
        if (from === 'studentList' && page) {
            navigate(`/academy/student/list?page=${page}`);
        } else {
            navigate(-1);
        }
    };

    return (
        <>
            <main className='flex flex-col flex-1'>
                <div className='pt-4'>
                    <button className='text-sm text-gray-600 hover:text-gray-800' onClick={handleBack}>← 뒤로가기</button>
                </div>
                <div className='flex flex-row justify-between items-center pt-8'>
                    <h1 className='heading1'>학생 관리 상세</h1>
                </div>
                <div className="mt-5">
                    <Tabs tabs={tabItems} activeId={activeTab} />
                    <div className="py-5">
                        {loading && <p className='text-gray-500'>로딩중...</p>}
                        {!!error && <p className='text-red-500'>{error}</p>}
                        {!loading && !error && (
                            renderTabContent()
                        )}
                    </div>
                </div>
            </main>
            <Modal
                title="학생 비밀번호 변경"
                open={passwordModalOpen}
                okText="변경"
                cancelText="취소"
                confirmLoading={changingPassword}
                onOk={handlePasswordChange}
                onCancel={handleClosePasswordModal}
                destroyOnClose
            >
                <div className='pt-2 space-y-4'>
                    <div>
                        <Input
                            label="새 비밀번호"
                            type="password"
                            placeholder='새 비밀번호를 입력해주세요'
                            value={passwordForm.newPassword}
                            autoComplete='new-password'
                            onChange={(e) => handlePasswordFieldChange('newPassword', e.target.value)}
                        />
                        <p className='mt-1 text-xs text-gray-500'>비밀번호는 최소 {MIN_PASSWORD_LENGTH}자 이상이어야 합니다.</p>
                        {passwordErrors.newPassword && (
                            <p className='mt-1 text-xs text-red-500'>{passwordErrors.newPassword}</p>
                        )}
                    </div>
                    <div>
                        <Input
                            label="새 비밀번호 확인"
                            type="password"
                            placeholder='새 비밀번호를 다시 입력해주세요'
                            value={passwordForm.confirmPassword}
                            autoComplete='new-password'
                            onChange={(e) => handlePasswordFieldChange('confirmPassword', e.target.value)}
                        />
                        {passwordErrors.confirmPassword && (
                            <p className='mt-1 text-xs text-red-500'>{passwordErrors.confirmPassword}</p>
                        )}
                    </div>
                </div>
            </Modal>
        </>
    )
}